import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography, TextField, IconButton, Button, Select, MenuItem, FormControl, InputAdornment, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Radio, Paper, CircularProgress } from '@mui/material';
import { DesktopDatePicker } from '@mui/lab';
import { Close, Save } from '@mui/icons-material';
import { GroupedStatuses, IAgentDraw, Status } from '../../../../models/agent-draws-and-deductions/agent-draw';
import { IAgentDrawsAndDeductionsAPI } from '../../../../fetch/agent-draws-and-deductions';

// Draw Financial Status: Not Paid
const NOT_PAID_STATUS_ID = 1;

interface AddPayload { date: string; amount: number; memo?: string; notifyLineLead?: boolean; requireAgentApproval?: boolean; targetDrawId?: number | null; }
interface EditPayload extends AddPayload { drawFinancialStatusId?: number | ''; }

interface Props {
  open: boolean;
  onClose: () => void;
  initialData?: IAgentDraw | null;
  statuses: GroupedStatuses<Status> | null;
  employeeId: number;
  onCreate: (payload: AddPayload) => Promise<void>;
  onEditSave: (drawId: number, payload: EditPayload) => Promise<void>;
  saving?: boolean;
  showNotifyLineLead?: boolean;
  showRequireAgentApproval?: boolean;
}

const formatCurrency = (value: number): string => {
  if (isNaN(value)) return '$0.00';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const denseLabelStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: '0.85rem',
  color: '#222',
  display: 'flex',
  alignItems: 'center',
  height: '100%'
};

// Stable date helper functions (module scope)
const toYmdLocal = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const todayLocalYmd = () => toYmdLocal(new Date());
const parseYmdToLocalDate = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export const AddEditAgentDrawModal: React.FC<Props> = ({ open, onClose, initialData, statuses, employeeId, onCreate, onEditSave, saving = false, showNotifyLineLead = false, showRequireAgentApproval = false }) => {
  const isEdit = useMemo(() => !!initialData, [initialData]);

  const [date, setDate] = useState<string>(todayLocalYmd());
  const [amount, setAmount] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  const [drawFinancialStatusId, setDrawFinancialStatusId] = useState<string>('');
  const [notifyLineLead, setNotifyLineLead] = useState<boolean>(true);
  const [requireAgentApproval, setRequireAgentApproval] = useState<boolean>(true);

  // Negative-draw target selection
  const [targetDrawId, setTargetDrawId] = useState<number | null>(null);
  const [notPaidDraws, setNotPaidDraws] = useState<IAgentDraw[]>([]);
  const [loadingNotPaidDraws, setLoadingNotPaidDraws] = useState<boolean>(false);

  const amountNumber = Number(amount);
  const initialAmount = Number(initialData?.amount ?? 0);
  // The negative workflow applies when creating a negative draw, or when editing a positive draw
  // to a negative amount (the conversion follows the same rules as creating a negative draw).
  // Editing a draw that was already negative keeps its standalone credit behavior.
  const needsTarget = !isNaN(amountNumber) && amountNumber < 0 && (!isEdit || initialAmount >= 0);

  const selectedTarget = needsTarget ? notPaidDraws.find((d) => d.id === targetDrawId) ?? null : null;
  const exceedsTargetBalance = selectedTarget != null && Math.abs(amountNumber) > selectedTarget.balance;

  const sanitizeAmountInput = (raw: string) => {
    let v = raw || '';
    // digits, dot and minus
    v = v.replace(/[^0-9.-]/g, '');
    // keep minus only once and only at the start
    const isNeg = v.includes('-');
    v = v.replace(/-/g, '');
    if (isNeg) v = '-' + v;
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      const head = v.slice(0, firstDot + 1);
      const tail = v.slice(firstDot + 1).replace(/\./g, '');
      v = head + tail;
    }
    return v;
  };

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      if (initialData.drawDateString) {
        const d = new Date(initialData.drawDateString);
        if (!isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          setDate(`${y}-${m}-${dd}`);
        }
      }
      setAmount(String(initialData.amount));
      setMemo(initialData.memo ?? '');
      setDrawFinancialStatusId(initialData.drawFinancialStatusId ? String(initialData.drawFinancialStatusId) : '');
      setNotifyLineLead(initialData.notifyLineLead === false ? false : true);
      setRequireAgentApproval(initialData.requireAgentApproval === false ? false : true);
      setTargetDrawId(null);
    } else {
      setDate(todayLocalYmd());
      setAmount('');
      setMemo('');
      setDrawFinancialStatusId('');
      setNotifyLineLead(true);
      setRequireAgentApproval(true);
      setTargetDrawId(null);
    }
  }, [open, initialData]);

  // When the negative workflow is active, load the agent's Not Paid draws so the user can pick
  // which draw's balance the negative credit applies to. Selection is cleared when not negative.
  useEffect(() => {
    if (!open || !needsTarget || !employeeId) {
      setTargetDrawId(null);
      return;
    }

    let cancelled = false;
    const fetchNotPaidDraws = async () => {
      setLoadingNotPaidDraws(true);
      try {
        const res = await IAgentDrawsAndDeductionsAPI.getDrawsByAgentId(employeeId, {
          page: 1,
          perPage: 1000,
          drawFinancialStatusFilter: NOT_PAID_STATUS_ID,
        });
        if (!cancelled) {
          // Only positive draws with a remaining balance can absorb a credit; a draw being
          // edited to a negative amount cannot absorb its own credit.
          setNotPaidDraws((res.records ?? []).filter((d) => d.amount > 0 && d.balance > 0 && d.id !== initialData?.id));
        }
      } catch {
        if (!cancelled) setNotPaidDraws([]);
      } finally {
        if (!cancelled) setLoadingNotPaidDraws(false);
      }
    };
    fetchNotPaidDraws();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsTarget, employeeId]);

  const handleSave = async () => {
    const amountNum = Number(amount);
    if (isNaN(amountNum)) return;
    // A negative amount must be assigned to an existing Not Paid draw with enough balance.
    if (needsTarget && (!targetDrawId || exceedsTargetBalance)) return;
    if (isEdit) {
      if (!initialData?.id) return;
      await onEditSave(initialData.id, { date, amount: amountNum, memo, drawFinancialStatusId: drawFinancialStatusId ? Number(drawFinancialStatusId) : '', notifyLineLead, requireAgentApproval, targetDrawId: needsTarget ? targetDrawId : null });
      return;
    }
    await onCreate({ date, amount: amountNum, memo, notifyLineLead, requireAgentApproval, targetDrawId: amountNum < 0 ? targetDrawId : null });
  };


  const financialStatuses = useMemo(() => (statuses?.drawFinancialStatus ?? []), [statuses]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          width: '720px',
          maxWidth: 'calc(100% - 48px)',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 1.5, bgcolor: '#005570', color: '#eff4f6' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{isEdit ? 'Edit Draw' : 'Add Draw'}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#eff4f6' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pt: 3, pb: 1, overflowX: 'hidden' }}>
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Date</Typography>
          </Grid>
          <Grid item xs={7}>
            <DesktopDatePicker
              inputFormat="M/d/yyyy"
              value={date ? parseYmdToLocalDate(date) : null}
              onChange={(d) => {
                if (d instanceof Date && !isNaN(d.getTime())) {
                  setDate(toYmdLocal(d));
                }
              }}
              renderInput={(params) => (
                <TextField {...params} name="date" fullWidth size="small" />
              )}
            />
          </Grid>

          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Amount</Typography>
          </Grid>
          <Grid item xs={7}>
            <TextField
              fullWidth
              name="amount"
              value={amount}
              onChange={(e) => setAmount(sanitizeAmountInput(e.target.value))}
              size="small"
              placeholder="e.g. -5000 or 2500.000"
              autoComplete="off"
              inputMode="decimal"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                },
                '& .MuiOutlinedInput-root.Mui-focused': {
                  bgcolor: 'background.paper',
                },
                '& input': {
                  backgroundColor: 'transparent !important',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                  WebkitTextFillColor: 'inherit',
                  caretColor: 'inherit',
                },
              }}
            />
          </Grid>

          {needsTarget && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#005570', display: 'block', mb: 0.5 }}>
                Select the Not Paid draw to apply this negative amount to
              </Typography>
              {loadingNotPaidDraws ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                  <CircularProgress size={18} />
                  <Typography variant="caption">Loading draws...</Typography>
                </div>
              ) : notPaidDraws.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  This agent has no Not Paid draws with a remaining balance to apply the credit to.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell sx={{ fontWeight: 700 }}>Draw ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Draw Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notPaidDraws.map((d) => (
                        <TableRow
                          key={d.id}
                          hover
                          selected={targetDrawId === d.id}
                          onClick={() => setTargetDrawId(d.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Radio
                              size="small"
                              checked={targetDrawId === d.id}
                              onChange={() => setTargetDrawId(d.id)}
                              value={d.id}
                            />
                          </TableCell>
                          <TableCell>{d.id}</TableCell>
                          <TableCell align="right">{formatCurrency(d.amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(d.balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {exceedsTargetBalance && selectedTarget && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  The negative amount {formatCurrency(Math.abs(amountNumber))} exceeds the balance of draw {selectedTarget.id} ({formatCurrency(selectedTarget.balance)}). Enter a smaller amount or select a different draw.
                </Typography>
              )}
            </Grid>
          )}

          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Memo</Typography>
          </Grid>
          <Grid item xs={7}>
            <TextField fullWidth name="memo" value={memo} onChange={(e) => setMemo(e.target.value)} size="small" multiline minRows={3} />
          </Grid>

          {showNotifyLineLead && (
            <>
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Notify Line Lead</Typography>
              </Grid>
              <Grid item xs={7} sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={notifyLineLead}
                  onChange={(e) => setNotifyLineLead(e.target.checked)}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                <Typography variant="body2">{notifyLineLead ? 'Yes' : 'No'}</Typography>
              </Grid>
            </>
          )}

          {showRequireAgentApproval && (
            <>
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Require Agent Approval</Typography>
              </Grid>
              <Grid item xs={7} sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={requireAgentApproval}
                  onChange={(e) => setRequireAgentApproval(e.target.checked)}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                  disabled={isEdit}
                />
                <Typography variant="body2">{requireAgentApproval ? 'Yes' : 'No'}</Typography>
              </Grid>
            </>
          )}

          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Draw Financial Status</Typography>
          </Grid>
          <Grid item xs={7}>
            <FormControl fullWidth size="small">
              <Select
                name="drawFinancialStatusId"
                value={drawFinancialStatusId}
                onChange={(e) => setDrawFinancialStatusId(String(e.target.value))}
                displayEmpty
                disabled={!isEdit || needsTarget}
              >
                <MenuItem value="">
                  <em>Select status</em>
                </MenuItem>
                {financialStatuses.map((s: Status) => (
                  <MenuItem key={s.value} value={s.value}>{s.description}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<Close fontSize="small" />}
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<Save fontSize="small" />}
          onClick={handleSave}
          disabled={saving || isNaN(Number(amount)) || (needsTarget && (!targetDrawId || exceedsTargetBalance))}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditAgentDrawModal;
